import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AMCReportTable from "./AMCReportTable";
import { useAppSelector } from "../../../redux/hooks";
import { formatAdType } from "../utils/utils";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import PieChart from "../../../components/core/blocks/PieChart";
import { metricColors } from "../../../components/custom/analytics/palettes";
import { Chart } from "@highcharts/react";
import { useAMCReportData } from "../hooks/useAMCReportData";
import { computeDerivedPlacementMetrics } from "../utils/placementMetrics";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import { formatMetricValue } from "../../../utils/formatNumber";
import Card from "../../../components/core/basic/Card";
import { Box, Grid, VStack, Wrap } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";

const TOTAL_METRICS = [
  "ad_sales_clicks",
  "ad_sales_views",
  "ad_spend",
  "impressions",
  "clicks",
  "user_purchased_clicks",
  "conversions_clicks",
  "conversions_views",
];

const CHART_METRIC_OPTIONS = [
  {
    value: "ad_sales",
    label: ($) => $.amc["Ad Sales"],
    formatOptions: { format: "currency" },
  },
  {
    value: "ad_spend",
    label: ($) => $.amc["Ad Spend"],
    formatOptions: { format: "currency" },
  },
  {
    value: "clicks",
    label: ($) => $.amc["Clicks"],
    formatOptions: { format: "number", decimalPlaces: 0 },
  },
  {
    value: "impressions",
    label: ($) => $.amc["Impressions"],
    formatOptions: { format: "number", decimalPlaces: 0 },
  },
  {
    value: "conversions",
    label: ($) => $.amc["Conversions"],
    formatOptions: { format: "number", decimalPlaces: 0 },
  },
];

const Metric = ({ title, value, formatOptions }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const formattedValue = formatMetricValue(value, { ...formatOptions, marketPlace });
  return (
    <Box fontSize={"medium"} textAlign="center">
      <span className={css({ whiteSpace: "nowrap" })}>{title}</span>
      <span className={css({ fontSize: "large", fontWeight: "bold", display: "block" })}>{formattedValue}</span>
    </Box>
  );
};

const groupByAdType = (data) => {
  if (!data) return {};
  const result = {};
  for (const row of data) {
    const adType = row.placement_type;
    if (!adType) continue;
    if (!result[adType]) {
      result[adType] = { str_cnt: 0, ...Object.fromEntries(TOTAL_METRICS.map((m) => [m, 0])) };
    }
    const totals = result[adType];
    totals.str_cnt += 1;
    for (const metric of TOTAL_METRICS) {
      totals[metric] += row[metric] || 0;
    }
  }
  for (const totals of Object.values(result)) {
    computeDerivedPlacementMetrics(totals);
  }
  return result;
};

const groupBySearchTermAndPlacement = (data) => {
  if (!data) return new Map();
  const result = new Map();
  for (const row of data) {
    const term = row.customer_search_term;
    const placement = row.placement_type;
    if (!term || !placement) continue;
    if (!result.has(term)) result.set(term, new Map());
    const placementMap = result.get(term);
    if (!placementMap.has(placement)) {
      placementMap.set(placement, { str_cnt: 0, ...Object.fromEntries(TOTAL_METRICS.map((m) => [m, 0])) });
    }
    const totals = placementMap.get(placement);
    totals.str_cnt += 1;
    for (const metric of TOTAL_METRICS) {
      totals[metric] += row[metric] || 0;
    }
  }
  for (const [, placementMap] of result) {
    for (const [, total] of placementMap) {
      computeDerivedPlacementMetrics(total);
    }
  }
  return result;
};

const StackedBarChart = ({ metric, formatter, series, categories, height }) => {
  const options = {
    title: { text: "" },
    chart: {
      height,
      type: "bar",
      style: {
        fontFamily: "Poppins",
        fontWeight: 300,
        fontSize: "15px",
      },
    },
    colors: [metricColors.metric1[1], metricColors.metric2[1], metricColors.metric3[1], metricColors.metric4[1]],
    xAxis: {
      labels: {
        useHTML: true,
        allowOverlap: true,
        style: {
          wordBreak: "normal",
          textOverflow: "allow",
          width: "150px",
          minWidth: "150px",
          textAlign: "right",
        },
      },
      categories,
    },
    legend: { enabled: true },
    yAxis: {
      title: {
        text: metric,
      },
    },
    tooltip: {
      formatter: function () {
        if (formatter) {
          return `${this.x}: ${formatter(this.y)}`;
        }
        return `${this.x}: ${this.y}`;
      },
    },
    plotOptions: {
      series: {
        stacking: "normal",
        dataLabels: {
          enabled: false,
          formatter: function () {
            return formatter(this.y);
          },
        },
      },
    },
    series,
  };
  return <Chart options={options} />;
};

const AMCSearchTermPlacementReport = ({ reportData, reportDetails, queryDetails }) => {
  const { t } = useTranslation(["amc"]);
  const [toggle, setToggle] = useState("keywords", "totals");
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const acosOrRoas = useAcosOrRoas();
  const [pieChartMetric, setPieChartMetric] = useState(CHART_METRIC_OPTIONS[0].value);
  const selectedMetric = useMemo(
    () => CHART_METRIC_OPTIONS.find((option) => option.value === pieChartMetric),
    [pieChartMetric],
  );
  const { data: totalReportData } = useAMCReportData(
    null,
    "PLACEMENT_PERFORMANCE_INTERNAL",
    true,
    reportDetails?.id,
    !!reportDetails?.id,
  );

  const buttonGroupOptions = useMemo(
    () => CHART_METRIC_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) })),
    [t],
  );

  const byAdType = useMemo(() => groupByAdType(totalReportData), [totalReportData]);
  const byKw = useMemo(() => groupBySearchTermAndPlacement(reportData), [reportData]);

  const pieChartData = useMemo(() => {
    return Object.keys(byAdType)
      .map((adType) => ({
        name: formatAdType(adType),
        y: byAdType[adType][pieChartMetric],
      }))
      .filter((type) => type.y);
  }, [byAdType, pieChartMetric]);

  const rowChartData = useMemo(() => {
    if (!byKw || byKw.size === 0) return { categories: [], series: [] };
    const mapping = [];
    for (const [kw, placementMap] of byKw) {
      let total = 0;
      const entry = { kw };
      for (const [adType, totals] of placementMap) {
        entry[adType] = totals[pieChartMetric] || 0;
        total += entry[adType];
      }
      entry.total = total;
      mapping.push(entry);
    }
    const sortedSeries = mapping.sort((a, b) => b.total - a.total).slice(0, 15);
    const categories = sortedSeries.map((item) => item.kw);
    return {
      categories,
      series: [
        {
          name: t(($) => $.amc["Top Of Search on-Amazon"]),
          data: sortedSeries.map((item) => item["Top of Search on-Amazon"] || 0),
        },
        {
          name: t(($) => $.amc["Other on-Amazon"]),
          data: sortedSeries.map((item) => item["Other on-Amazon"] || 0),
        },
        {
          name: t(($) => $.amc["Detail Page on-Amazon"]),
          data: sortedSeries.map((item) => item["Detail Page on-Amazon"] || 0),
        },
        {
          name: t(($) => $.amc["Off Amazon"]),
          data: sortedSeries.map((item) => item["Off Amazon"] || 0),
        },
      ],
    };
  }, [byKw, pieChartMetric, t]);

  return (
    <>
      <Grid columns={{ base: 1, sm: 2 }}>
        <VStack gap={"small"}>
          <ButtonGroup
            buttons={[
              { value: "keywords", label: t(($) => $.amc["Top Keywords"]) },
              { value: "totals", label: t(($) => $.amc["Total"]) },
            ]}
            value={toggle}
            onSelect={(value) => setToggle(value)}
            colorPalette={"buttonGroup.fuchsia"}
          />
          {toggle === "totals" && (
            <PieChart
              data={pieChartData}
              nameAccessor={(d) => d.name}
              accessor={(d) => d.y}
              formatter={(v) => formatMetricValue(v, { ...selectedMetric.formatOptions, marketPlace })}
              height={460}
            />
          )}
          {toggle === "keywords" && (
            <StackedBarChart
              height={500}
              metric={t(selectedMetric.label)}
              formatter={(v) => formatMetricValue(v, { ...selectedMetric.formatOptions, marketPlace })}
              series={rowChartData.series}
              categories={rowChartData.categories}
            />
          )}
          <ButtonGroup
            buttons={buttonGroupOptions}
            value={selectedMetric.value}
            onSelect={(value) => setPieChartMetric(value)}
            colorPalette={"buttonGroup.fuchsia"}
          />
        </VStack>
        <VStack gap="small">
          {Object.keys(byAdType)
            .sort((a, b) => byAdType[b].impressions - byAdType[a].impressions)
            .map((adType) => {
              const total = byAdType[adType];
              return (
                <Card key={adType} variant="flat" padding={"medium"}>
                  <h4
                    className={css({
                      margin: 0,
                      fontWeight: "bold",
                      fontSize: "medium",
                      marginLeft: "medium",
                    })}
                  >
                    {formatAdType(adType)}
                  </h4>
                  <Wrap columnGap="large" rowGap={"small"} justifyContent="space-around">
                    <Metric
                      title={t(($) => $.amc["Ad Sales"])}
                      value={total["ad_sales"]}
                      formatOptions={{ format: "currency", compact: true }}
                    />
                    <Metric
                      title={t(($) => $.amc["Ad Spend"])}
                      value={total["ad_spend"]}
                      formatOptions={{ format: "currency", compact: true }}
                    />
                    <Metric
                      title={t(($) => $.amc["Impressions"])}
                      value={total["impressions"]}
                      formatOptions={{ format: "number", decimalPlaces: 0 }}
                    />
                    <Metric
                      title={t(($) => $.amc["Clicks"])}
                      value={total["clicks"]}
                      formatOptions={{ format: "number", decimalPlaces: 0 }}
                    />
                    {acosOrRoas(
                      <Metric
                        title={t(($) => $.amc["ACOS"])}
                        value={total["acos"]}
                        formatOptions={{ format: "percent" }}
                      />,
                      <Metric
                        title={t(($) => $.amc["ROAS"])}
                        value={total["roas"]}
                        formatOptions={{ format: "number" }}
                      />,
                    )}
                    <Metric title={t(($) => $.amc["CTR"])} value={total["ctr"]} formatOptions={{ format: "percent" }} />
                    <Metric
                      title={t(($) => $.amc["Conversion Rate"])}
                      value={total["conv_rate"]}
                      formatOptions={{ format: "percent" }}
                    />
                    <Metric
                      title={t(($) => $.amc["CPC"])}
                      value={total["cost_per_click"]}
                      formatOptions={{ format: "currency", compact: true }}
                    />
                  </Wrap>
                </Card>
              );
            })}
        </VStack>
      </Grid>
      <AMCReportTable reportData={reportData} reportDetails={reportDetails} queryDetails={queryDetails} />
    </>
  );
};

export default AMCSearchTermPlacementReport;
